import {
  cn,
  FormControl,
  FormControlError,
  FormControlErrorText,
  Text,
  Textarea,
  TextareaInput,
  useTheme,
  type InputSizeKey,
} from '@vidahealth/vida-ui';
import { useColorScheme } from 'nativewind';
import { useState, type ComponentProps, type ReactNode } from 'react';
import { Controller, type Control, type FieldPath, type FieldValues } from 'react-hook-form';
import type { StyleProp, TextStyle } from 'react-native';

import {
  resolveTextAreaBorderColor,
  resolveTextAreaContainerStyle,
  resolveTextAreaFieldStyle,
} from './utils/resolveTextAreaStyle';

type TextareaInputProps = ComponentProps<typeof TextareaInput>;

type CommonProps = {
  size?: InputSizeKey;
  /**
   * Optional transform applied to every keystroke before the value is written
   * to the form state. Useful for stripping disallowed characters.
   */
  format?: (raw: string) => string;
  /**
   * Static label rendered above the field. Unlike `TextField`'s animated
   * `FloatingLabel`, this never overlaps the field — a textarea is too tall
   * for a label to rest inside it, so there's no "floated vs. resting"
   * position to animate between. Omit to fall back to a plain native
   * placeholder with no persistent label.
   */
  label?: ReactNode;
  /**
   * Extra Tailwind classes merged onto the inner <TextareaInput>. Use for
   * layout tweaks like padding overrides. Cannot beat the theme-resolved
   * padding/font size (passed via the inline style array); reach for
   * `inputFieldStyle` when a numeric override is needed.
   */
  inputFieldClassName?: string;
  /**
   * Inline style merged onto the inner <TextareaInput>. Required for
   * overriding the theme-driven padding/font size (passed via its style
   * array, so it wins over Tailwind classes).
   */
  inputFieldStyle?: StyleProp<TextStyle>;
  /**
   * Renders the field greyed + non-editable. The FormControl's `disabled`
   * attribute greys it (and blocks focus on web), and `editable={false}` is
   * mapped by vida-ui to the web `readOnly` attribute.
   */
  isDisabled?: boolean;
};

type RhfProps<TFieldValues extends FieldValues> = {
  control: Control<TFieldValues>;
  name: FieldPath<TFieldValues>;
  value?: never;
  onChangeText?: never;
  errorMessage?: never;
};

type ControlledProps = {
  control?: never;
  name?: never;
  value: string;
  onChangeText: (text: string) => void;
  errorMessage?: string;
};

export type TextAreaProps<TFieldValues extends FieldValues> = Omit<
  TextareaInputProps,
  'value' | 'onChangeText' | 'onBlur'
> &
  CommonProps &
  (RhfProps<TFieldValues> | ControlledProps) & {
    onBlur?: () => void;
  };

/**
 * Multiline free-text field — the counterpart to `TextField` for open-ended
 * answers (dynamic-assessment `comment` elements). Kept as its own component
 * rather than a `multiline` mode on `TextField`: gluestack itself ships
 * `Textarea` as a sibling family with its own style context rather than an
 * `Input` variant, and folding it into `TextField` previously produced two
 * concrete bugs — a label-color state that forked from the label-position
 * state, and a `size` prop that meant a theme-resolved token lookup on one
 * branch but a bare Tailwind class on the other (see PR #73 review).
 */
export function TextArea<TFieldValues extends FieldValues>(props: TextAreaProps<TFieldValues>) {
  if ('control' in props && props.control) {
    const { control, name, ...rest } = props;
    return (
      <Controller
        control={control}
        name={name}
        render={({ field, fieldState }) => (
          <TextAreaInner
            {...rest}
            value={(field.value as string | undefined) ?? ''}
            onChangeText={(text) => {
              const next = rest.format ? rest.format(text) : text;
              field.onChange(next);
            }}
            onBlur={() => {
              field.onBlur();
              rest.onBlur?.();
            }}
            errorMessage={fieldState.error?.message}
          />
        )}
      />
    );
  }
  const { value, onChangeText, errorMessage, format, ...rest } = props as ControlledProps &
    Omit<TextAreaProps<TFieldValues>, keyof RhfProps<TFieldValues>>;
  return (
    <TextAreaInner
      {...rest}
      value={value}
      onChangeText={(text) => onChangeText(format ? format(text) : text)}
      errorMessage={errorMessage}
    />
  );
}

type InnerProps = CommonProps &
  Omit<TextareaInputProps, 'value' | 'onChangeText' | 'onBlur'> & {
    value: string;
    onChangeText: (text: string) => void;
    onBlur?: () => void;
    errorMessage?: string;
  };

function TextAreaInner({
  size = 'lg',
  label,
  value,
  onChangeText,
  onBlur,
  errorMessage,
  onFocus,
  inputFieldClassName,
  inputFieldStyle,
  isDisabled,
  placeholder,
  format: _format,
  ...inputProps
}: InnerProps) {
  const theme = useTheme();
  const { colorScheme } = useColorScheme();
  const [isFocused, setIsFocused] = useState(false);

  const hasError = !!errorMessage;
  // No "resting vs. floated" position to key off (unlike TextField's
  // FloatingLabel) — the label is always in the same place, so its color
  // only needs to reflect focus/error, not a filled-vs-empty distinction.
  const labelColorClass = hasError
    ? 'text-error-700'
    : isFocused
      ? 'text-primary-500'
      : 'text-typography-700';

  const borderColor = resolveTextAreaBorderColor(
    theme,
    hasError ? 'invalid' : isFocused ? 'focused' : 'default',
    colorScheme === 'dark' ? 'dark' : 'light'
  );

  // Single source for both accessibility props below — gluestack's
  // TextareaInput defaults its own `aria-label` to "Input Field" and
  // re-applies that default AFTER the props spread, so passing `undefined`
  // (rather than omitting the attribute) lets the default win over
  // `accessibilityLabel` on web. Giving both the same fallback keeps them
  // from drifting apart the way they did here (see PR #73 review, third
  // pass): with no distinct `label`, every `comment` field would otherwise
  // announce as "Input Field" instead of its placeholder.
  const accessibleName =
    typeof label === 'string' ? label : typeof placeholder === 'string' ? placeholder : undefined;

  return (
    <FormControl isInvalid={hasError} isDisabled={isDisabled}>
      {label ? (
        <Text size='sm' weight='medium' className={cn('mb-1 ml-1', labelColorClass)}>
          {label}
        </Text>
      ) : null}
      <Textarea size={size} style={[resolveTextAreaContainerStyle(theme, size), { borderColor }]}>
        <TextareaInput
          className={cn('placeholder:text-typography-500', inputFieldClassName)}
          style={[resolveTextAreaFieldStyle(theme, size), inputFieldStyle]}
          aria-label={accessibleName}
          accessibilityLabel={accessibleName}
          placeholder={placeholder}
          value={value}
          onChangeText={onChangeText}
          onFocus={(e) => {
            setIsFocused(true);
            onFocus?.(e);
          }}
          onBlur={() => {
            setIsFocused(false);
            onBlur?.();
          }}
          // vida-ui maps `editable` to the web `readOnly` attribute; combined
          // with the FormControl's `disabled` attribute (from isDisabled),
          // the field is genuinely non-editable on web + native.
          editable={!isDisabled}
          {...inputProps}
        />
      </Textarea>
      {errorMessage ? (
        <FormControlError>
          <FormControlErrorText size='sm' className='ml-1 mt-1'>
            {errorMessage}
          </FormControlErrorText>
        </FormControlError>
      ) : null}
    </FormControl>
  );
}
